import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'hp_viewed_listings';

let cachedIds: Set<string> | null = null;
const listeners = new Set<() => void>();

function readFromStorage(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw) as Array<string>) : new Set();
  } catch {
    return new Set();
  }
}

function getSnapshot(): Set<string> {
  if (!cachedIds) {
    cachedIds = readFromStorage();
  }
  return cachedIds;
}

const emptySet = new Set<string>();
function getServerSnapshot(): Set<string> {
  return emptySet;
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function persistAndNotify(ids: Set<string>): void {
  cachedIds = ids;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // sessionStorage full or unavailable
  }
  listeners.forEach((l) => l());
}

export function useViewedListings() {
  const viewedIds = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const markViewed = useCallback((markerId: string) => {
    const current = getSnapshot();
    if (current.has(markerId)) return;
    const next = new Set(current);
    next.add(markerId);
    persistAndNotify(next);
  }, []);

  const isViewed = useCallback(
    (markerId: string) => viewedIds.has(markerId),
    [viewedIds],
  );

  return { viewedIds, markViewed, isViewed };
}
